import * as React from "react";
import { useDispatch } from "react-redux";
import {
  updateActiveFormInstance,
  useFormError,
  useFormInstanceField,
} from "../../../store/base";
import {
  constants as invoicingProductConstants,
  performFilter as invoicingProductPerformFilter,
  constructStrRep as invoicingProductConstructStrRep,
  useInvoicingProductPagination,
} from "../../../store/invoicingProducts";
import {
  constants as invoicingCostCenterConstants,
  performFilter as invoicingCostCenterPerformFilter,
  constructStrRep as invoicingCostCenterConstructStrRep,
  useInvoicingCostCenterPagination,
  useInvoicingCostCenter,
} from "../../../store/invoicingCostCenters";
import {
  constants as invoicingProjectConstants,
  performFilter as invoicingProjectPerformFilter,
  constructStrRep as invoicingProjectConstructStrRep,
  useInvoicingProjectPagination,
  useInvoicingProject,
} from "../../../store/invoicingProjects";
import invoicingProductListDefs from "../../Lists/InvoicingProducts/listDefs";
import invoicingCostCentersListDefs from "../../Lists/InvoicingCostCenters/listDefs";
import invoicingProjectsListDefs from "../../Lists/InvoicingProjects/listDefs";
import { addToast, TOAST_TYPES } from "../../../store/toasts";
import Table from "../../Billecta/Table/BasicTable";
import {
  OverviewSubtitle,
  OverviewTitle,
  OverviewTitleWithSubtitleWrapper,
  OverviewTitleWrapper,
} from "../../Details/OverviewInfo/styles";
import { PrimaryButton, TextButton } from "../Base/Buttons";
import RelationalTableField from "../Base/FieldComponents/RelationalTableField";
import {
  DatePicker,
  RadioGroup,
  RentField,
  Select,
  TextInput,
} from "../Base/Fields";
import { InnerBox } from "../../sharedStyles";
import { toMoneyString } from "../../utils/stringUtils";

import { OrderUp, OrderDown } from "../../Details/OverviewInfo/Costs/styles";

import ProductNestedChildrenMethod from "../InvoicingProduct/NestedChildren";
import CostCenterNestedChildrenMethod from "../InvoicingCostCenter/NestedChildren";
import ProjectNestedChildrenMethod from "../InvoicingProject/NestedChildren";
import { StatusLabel } from "../../Lists/Base/CompleteList/styles";
import { InfoBox } from "../../Displays";
import { cloneDeep } from "lodash";

export default function ContractCostsBase({
  method,
  skipInvoicing,
  goBackToInvoicingSetup,
  contract,
  constants,
}) {
  const dispatch = useDispatch();
  const storeName = constants.STORE_NAME;
  const [handleCostIndex, setHandleCostIndex] = React.useState(null);

  const topRef = React.useRef();

  const totalArea = contract?.total_area || null;

  const defaultSetting = useFormInstanceField({
    storeName,
    fieldKey: "setting",
  });

  const costs =
    useFormInstanceField({
      storeName,
      fieldKey: "cost_set",
    }) || [];

  const baseVAT = useFormInstanceField({
    storeName,
    fieldKey: "vat",
  });

  const costErrors = useFormError({
    storeName,
    fieldKey: "cost_set",
  });

  const [defaultProject] = useInvoicingProject(defaultSetting?.project?.id);
  const [defaultCostCenter] = useInvoicingCostCenter(
    defaultSetting?.cost_center?.id
  );

  const goToTop = () => {
    setTimeout(() => {
      topRef.current &&
        topRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
  };

  const handleRowDown = (event, row) => {
    event.stopPropagation();

    const instanceClone = cloneDeep(costs);

    const rowOrder = row.order;
    const newOrder = rowOrder + 1;

    const rowThatChangedOrder = instanceClone.find((r) => r.order === rowOrder);
    const rowThatPreviuoslyHadOrder = instanceClone.find(
      (r) => r.order === newOrder
    );

    rowThatChangedOrder.order = newOrder;
    rowThatPreviuoslyHadOrder.order = rowOrder;

    instanceClone.sort((a, b) => {
      return a.order - b.order;
    });

    dispatch(
      updateActiveFormInstance({
        storeName,
        data: {
          cost_set: instanceClone,
        },
      })
    );
  };

  const handleRowUp = (event, row) => {
    event.stopPropagation();

    const instanceClone = cloneDeep(costs);

    const rowOrder = row.order;
    const newOrder = rowOrder - 1;

    const rowThatChangedOrder = instanceClone.find((r) => r.order === rowOrder);
    const rowThatPreviuoslyHadOrder = instanceClone.find(
      (r) => r.order === newOrder
    );

    rowThatChangedOrder.order = newOrder;
    rowThatPreviuoslyHadOrder.order = rowOrder;

    instanceClone.sort((a, b) => {
      return a.order - b.order;
    });

    dispatch(
      updateActiveFormInstance({
        storeName,
        data: {
          cost_set: instanceClone,
        },
      })
    );
  };

  const tableCosts = React.useMemo(() => {
    return costs;
  }, [costs]);

  const tableColumns = React.useMemo(() => [
    {
      Header: " ",
      id: "orderOnInvoice",
      Cell: ({ row }) => {
        const rowOrder = row.original.order;
        const allOrders = costs.map((fc) => fc.order);
        const maxUp = Math.max.apply(null, allOrders);
        const maxDown = Math.min.apply(null, allOrders);
        const canDown = rowOrder < maxUp;
        const canUp = rowOrder > maxDown;

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {canUp && <OrderUp onClick={(e) => handleRowUp(e, row.original)} />}
            {canDown && (
              <OrderDown onClick={(e) => handleRowDown(e, row.original)} />
            )}
          </div>
        );
      },
    },
    {
      Header: "Typ",
      accessor: "_",
      Cell: ({ row }) => {
        const isDiscount = row.original.value < 0;
        return (
          <StatusLabel state={isDiscount ? 3 : 1}>
            {isDiscount ? "Rabatt" : "Kostnad"}
          </StatusLabel>
        );
      },
    },
    {
      Header: "Titel",
      accessor: "title",
      Cell: ({ value }) => {
        return <div>{value}</div>;
      },
    },
    {
      Header: "Produkt",
      accessor: "product",
      Cell: ({ value }) => {
        const title = value?.title || "";

        return <div>{title}</div>;
      },
    },
    {
      Header: "SEK/månad",
      accessor: "value",
      Cell: ({ value }) => {
        return <div>{toMoneyString(value || 0)}</div>;
      },
    },
    {
      Header: "Momssats (%)",
      accessor: "vat",
      Cell: ({ value, row }) => {
        const productVat = row.original.product?.vat;
        const val =
          value != null
            ? value
            : productVat != null
            ? `${productVat} (från produkt)`
            : baseVAT != null
            ? `${baseVAT} (från grundmomssats)`
            : "-";
        return <div>{val}</div>;
      },
    },
    {
      Header: "Börjar aviseras för",
      accessor: "start_date",
      Cell: ({ value }) => {
        if (!value) return <div>Med avtalet</div>;
        return <div>{value}</div>;
      },
    },
    {
      Header: "Slutar aviseras för",
      accessor: "end_date",
      Cell: ({ value }) => {
        if (!value) return <div>Med avtalet</div>;

        return <div>{value}</div>;
      },
    },
    {
      Header: "Kostnadsställe",
      accessor: "cost_center",
      Cell: ({ value }) => {
        const title = value?.title
          ? value.title
          : defaultCostCenter
          ? `${defaultCostCenter.str_representation} (från inställning)`
          : "-";

        return <div>{title}</div>;
      },
    },
    {
      Header: "Projekt",
      accessor: "project",
      Cell: ({ value }) => {
        const title = value?.title
          ? value.title
          : defaultProject
          ? `${defaultProject.str_representation} (från inställning)`
          : "-";

        return <div>{title}</div>;
      },
    },
    {
      Header: "Indexuppräkning",
      accessor: "indexate",
      Cell: ({ value }) => {
        const label = value ? (
          <StatusLabel state={1}>Aktiv</StatusLabel>
        ) : (
          <StatusLabel state={3}>Inaktiv</StatusLabel>
        );

        return label;
      },
    },
  ]);

  const handleEditCost = (row) => {
    setHandleCostIndex(row.index);
  };

  const handleAddCost = () => {
    const costsCopy = [...costs];
    const newIndex = costsCopy.length;
    costsCopy.push({ order: newIndex + 1 });
    dispatch(
      updateActiveFormInstance({
        storeName,
        data: {
          cost_set: costsCopy,
        },
      })
    );

    setHandleCostIndex(newIndex);
  };

  const handleRemoveCost = (index) => {
    const costsCopy = cloneDeep(costs);
    const removedRowOrder = costsCopy[index].order;
    costsCopy.splice(index, 1);

    // fix orders
    if (costsCopy.length) {
      costsCopy.forEach((c) => {
        if (c.order > removedRowOrder) {
          c.order = c.order - 1;
        }
      });
    }

    setHandleCostIndex(null);

    dispatch(
      updateActiveFormInstance({
        storeName,
        data: {
          cost_set: costsCopy,
        },
      })
    );

    dispatch(
      addToast({
        type: TOAST_TYPES.INFO,
        title: "Raden togs bort",
      })
    );

    goToTop();
  };

  const handleEditCostDone = () => {
    dispatch(
      addToast({
        type: TOAST_TYPES.INFO,
        title: "Raden uppdaterades",
      })
    );
    setHandleCostIndex(null);

    goToTop();
  };

  const renderCostErrors = () => {
    return (
      <InfoBox
        title="En eller flera kostnadsrader innehåller fel"
        text="Kontrollera de rödmarkerade raderna. Klicka på raden för att se detaljer kring felet"
        boxTheme="warning"
      />
    );
  };

  const checkRowError = (row) => {
    const errorIndexes =
      costErrors
        ?.map((r, idx) => {
          if (Object.keys(r).length) {
            return idx;
          }
          return null;
        })
        ?.filter((e) => e != null) || [];
    return errorIndexes.includes(row.index);
  };

  const setProductCategoryAsTitle = (data) => {
    if (!costs[handleCostIndex] || !data) return;

    dispatch(
      updateActiveFormInstance({
        storeName,
        data: {
          [`cost_set[${handleCostIndex}].title`]: data.category_display,
        },
      })
    );

    dispatch(
      addToast({
        type: TOAST_TYPES.INFO,
        title: "Titeln uppdateraded",
        description:
          "Titeln på debiteringsraden sattes till kategorin på den valda produkten",
      })
    );
  };

  if (handleCostIndex != null) {
    return (
      <>
        <OverviewTitleWrapper>
          <OverviewTitle small>
            Hantera rad {costs[handleCostIndex]?.title || ""}
          </OverviewTitle>
        </OverviewTitleWrapper>

        <RelationalTableField
          storeName={storeName}
          tableStoreName={invoicingProductConstants.STORE_NAME}
          paginationMethod={useInvoicingProductPagination}
          performFilter={invoicingProductPerformFilter}
          listDefs={invoicingProductListDefs}
          title="Välj produkt..."
          fieldTitle="Produkt"
          method={method}
          fieldKey={`cost_set[${handleCostIndex}].product`}
          instructionsKey={"cost_set.product"}
          constructStrRep={invoicingProductConstructStrRep}
          selectedCallback={(data) => setProductCategoryAsTitle(data)}
          modalTitle="Hantera produkt"
          children={ProductNestedChildrenMethod({
            storeName,
            method,
            parentPath: `cost_set[${handleCostIndex}].product`,
            parentInstructionsPath: `cost_set.product`,
          })}
        />

        <TextInput
          title="Beskrivning"
          storeName={storeName}
          method={method}
          fieldKey={`cost_set[${handleCostIndex}].title`}
          instructionsKey="cost_set.title"
        />

        <RentField
          {...{ method, storeName }}
          fieldKey={`cost_set[${handleCostIndex}].value`}
          instructionsKey="cost_set.value"
          title="Debiteras (SEK)"
          subtitle="Rabatter anges negativt"
          description="Värde på debiteringen i SEK. Rabatter anges negativt, t.ex. -1000"
          area={totalArea}
        />

        <InnerBox style={{ marginBottom: 24 }}>
          <InfoBox
            boxTheme="info"
            text={`Som standard gäller raden under hela avtalet. Välj ett startdatum för att skriva över.`}
          />
          <DatePicker
            noMargin
            title="Startdatum"
            subtitle="Lämna tom om raden gäller från avtalets start"
            description="Kostnaden ska faktureras från detta datum. Lämnas tomt om kostnaden löper med avtalet."
            storeName={storeName}
            method={method}
            fieldKey={`cost_set[${handleCostIndex}].start_date`}
            instructionsKey="cost_set.start_date"
          />
        </InnerBox>

        <InnerBox style={{ marginBottom: 24 }}>
          <InfoBox
            boxTheme="info"
            text={`Som standard gäller raden under hela avtalet. Välj ett slutdatum för att skriva över.`}
          />
          <DatePicker
            noMargin
            title="Slutdatum"
            subtitle="Lämna tom om raden gäller till avtalets slut"
            description="Kostnaden ska faktureras till detta datum. Lämnas tomt om kostnaden löper med avtalet."
            storeName={storeName}
            method={method}
            fieldKey={`cost_set[${handleCostIndex}].end_date`}
            instructionsKey="cost_set.end_date"
          />
        </InnerBox>

        {baseVAT != null ? (
          <InnerBox style={{ marginBottom: 24 }}>
            <InfoBox
              boxTheme="info"
              text={`Standardmoms vald på avtalet: ${baseVAT}%. Välj en momssats för att skriva över.`}
            />
            <Select
              noMargin
              title="Momssats"
              description="Momssats som ska appliceras på kostnaden. Lämnas tomt om grundmomssatsen ska appliceras på kostnaden."
              storeName={storeName}
              method={method}
              fieldKey={`cost_set[${handleCostIndex}].vat`}
              instructionsKey="cost_set.vat"
            />
          </InnerBox>
        ) : (
          <Select
            title="Momssats"
            description="Momssats som ska appliceras på kostnaden. Lämnas tomt om grundmomssatsen ska appliceras på kostnaden."
            storeName={storeName}
            method={method}
            fieldKey={`cost_set[${handleCostIndex}].vat`}
            instructionsKey="cost_set.vat"
          />
        )}

        {defaultProject ? (
          <>
            <InnerBox style={{ marginBottom: 24 }}>
              <InfoBox
                boxTheme="info"
                text={`Standardprojekt från den valda aviseringsinställningen: ${defaultProject.str_representation}. Välj ett projekt för att skriva över standardprojektet.`}
              />

              <RelationalTableField
                noMargin
                storeName={storeName}
                tableStoreName={invoicingProjectConstants.STORE_NAME}
                paginationMethod={useInvoicingProjectPagination}
                performFilter={invoicingProjectPerformFilter}
                listDefs={invoicingProjectsListDefs}
                title="Välj projekt..."
                fieldTitle="Projekt"
                method={method}
                fieldKey={`cost_set[${handleCostIndex}].project`}
                instructionsKey={"cost_set.project"}
                constructStrRep={invoicingProjectConstructStrRep}
                modalTitle="Hantera projekt"
                children={ProjectNestedChildrenMethod({
                  storeName,
                  method,
                  parentPath: `cost_set[${handleCostIndex}].project`,
                  parentInstructionsPath: `cost_set.project`,
                })}
              />
            </InnerBox>
          </>
        ) : (
          <RelationalTableField
            storeName={storeName}
            tableStoreName={invoicingProjectConstants.STORE_NAME}
            paginationMethod={useInvoicingProjectPagination}
            performFilter={invoicingProjectPerformFilter}
            listDefs={invoicingProjectsListDefs}
            title="Välj projekt..."
            fieldTitle="Projekt"
            method={method}
            fieldKey={`cost_set[${handleCostIndex}].project`}
            instructionsKey={"cost_set.project"}
            constructStrRep={invoicingProjectConstructStrRep}
            modalTitle="Hantera projekt"
            children={ProjectNestedChildrenMethod({
              storeName,
              method,
              parentPath: `cost_set[${handleCostIndex}].project`,
              parentInstructionsPath: `cost_set.project`,
            })}
          />
        )}

        {defaultCostCenter ? (
          <InnerBox style={{ marginBottom: 24 }}>
            <InfoBox
              boxTheme="info"
              text={`Standardkostnadsställe från den valda aviseringsinställningen: ${defaultCostCenter.str_representation}. Välj ett kostnadsställe för att skriva över standardkostandsstället.`}
            />

            <RelationalTableField
              noMargin
              storeName={storeName}
              tableStoreName={invoicingCostCenterConstants.STORE_NAME}
              paginationMethod={useInvoicingCostCenterPagination}
              performFilter={invoicingCostCenterPerformFilter}
              listDefs={invoicingCostCentersListDefs}
              title="Välj kostnadsställe..."
              fieldTitle="Kostnadsställe"
              method={method}
              fieldKey={`cost_set[${handleCostIndex}].cost_center`}
              instructionsKey={"cost_set.cost_center"}
              constructStrRep={invoicingCostCenterConstructStrRep}
              children={CostCenterNestedChildrenMethod({
                storeName,
                method,
                parentPath: `cost_set[${handleCostIndex}].cost_center`,
                parentInstructionsPath: `cost_set.cost_center`,
              })}
              modalTitle="Hantera kostnadsställe"
            />
          </InnerBox>
        ) : (
          <RelationalTableField
            storeName={storeName}
            tableStoreName={invoicingCostCenterConstants.STORE_NAME}
            paginationMethod={useInvoicingCostCenterPagination}
            performFilter={invoicingCostCenterPerformFilter}
            listDefs={invoicingCostCentersListDefs}
            title="Välj kostnadsställe..."
            fieldTitle="Kostnadsställe"
            method={method}
            fieldKey={`cost_set[${handleCostIndex}].cost_center`}
            instructionsKey={"cost_set.cost_center"}
            constructStrRep={invoicingCostCenterConstructStrRep}
            children={CostCenterNestedChildrenMethod({
              storeName,
              method,
              parentPath: `cost_set[${handleCostIndex}].cost_center`,
              parentInstructionsPath: `cost_set.cost_center`,
            })}
            modalTitle="Hantera kostnadsställe"
          />
        )}

        <RadioGroup
          title="Indexuppräkna denna debiteringsrad"
          defaultValue={false}
          description="Räkna upp värdet på denna debiteringsrad utifrån inställningarna på avtalet"
          options={[
            {
              label: "Ja, indexuppräkna denna debiteringsrad",
              value: true,
            },
            {
              label: "Nej, indexuppräkna EJ denna debiteringsrad",
              value: false,
            },
          ]}
          {...{
            storeName,
            method,
            fieldKey: `cost_set[${handleCostIndex}].indexate`,
            instructionsKey: "cost_set.indexate",
          }}
        />

        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <TextButton
            title="Ta bort rad"
            red
            clicked={() => handleRemoveCost(handleCostIndex)}
          />
          <PrimaryButton title="Klar" clicked={handleEditCostDone} />
        </div>
      </>
    );
  }

  return (
    <>
      {skipInvoicing && (
        <InfoBox
          boxTheme="info"
          title="Avtalet sätts ej upp för avisering"
          text={`Du har valt att ej sätta upp avtalet för avisering. Tryck på "Sätt upp för avisering" för att gå tillbaka och välja aviseringsinställning.`}
          renderButton={{
            title: "Sätt upp för avisering",
            iconType: "arrow-back",
            clicked: goBackToInvoicingSetup,
          }}
        />
      )}

      <OverviewTitleWrapper style={{ scrollMargin: 50 }} ref={topRef}>
        <OverviewTitleWithSubtitleWrapper>
          <OverviewTitle small>Kostnader & Rabatter</OverviewTitle>
          <OverviewSubtitle>
            Tryck på en rad för att redigera eller ta bort den
          </OverviewSubtitle>
        </OverviewTitleWithSubtitleWrapper>
      </OverviewTitleWrapper>
      <Select
        title="Grundmomssats (%)"
        description="Momssats som ska ligga till grund för alla rader som EJ har en momssats specificerad. Om en rad har en momssats specificerad gäller den."
        {...{ storeName, method, fieldKey: "vat" }}
      />

      {costErrors?.length && renderCostErrors()}

      {costs?.length > 0 ? (
        <>
          <Table
            onRowClicked={handleEditCost}
            columns={tableColumns}
            data={tableCosts}
            checkRowError={checkRowError}
            hideSearch
          />
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <PrimaryButton title="Lägg till rad" clicked={handleAddCost} />
          </div>
        </>
      ) : (
        <InnerBox
          style={{
            minHeight: 200,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          Inga kostnader eller rabatter är tillagda än
          <div style={{ marginTop: 24 }}>
            <PrimaryButton title="Lägg till rad" clicked={handleAddCost} />
          </div>
        </InnerBox>
      )}
    </>
  );
}
